<template>
  <v-card 
    flat
    class="rounded-lg ma-1"
  >
    <v-card-title class="d-flex align-center">
      Requisições de Compra
      <NewRequestDialog />
    </v-card-title>

    <v-card-text>
      <v-tabs
        v-model="tab"
        align-tabs="center"
        color="red-darken-4"
      >
        <v-tab :value="1">Requisições</v-tab>
        <v-tab :value="2">Em Compras</v-tab>
      </v-tabs>

      <div class="d-flex justify-end py-4 px-2 ga-2">
        <v-spacer></v-spacer>
        <v-text-field
          v-model="search"
          density="compact"
          label="Procurar"
          prepend-inner-icon="mdi-magnify"
          variant="solo-filled"
          flat
          hide-details
          single-line
        ></v-text-field>
      </div>
      
      <v-window v-model="tab">
        <!-- PENDING -->
        <v-window-item :value="1">
          <v-data-table 
            v-model:search="search" 
            :items="pendingRequests"
            :headers="headers"
          >
            <template v-slot:[`item.priority`]="{ item }">
              <v-chip :color="priorityColor(item)">
                {{ priorityLabel(item) }}
              </v-chip>
            </template>
            <template v-slot:[`item.action`]="{ item }">
              <v-btn
                class="text-none font-weight-regular"
                icon="mdi-eye"
                variant="plain"
                size="small"
                @click="viewRequest(item)"
              ></v-btn>
              <v-btn
                class="text-none font-weight-regular"
                icon="mdi-pencil"
                variant="plain"
                size="small"
                @click="editRequest(item)"
              ></v-btn>
              <v-btn
                class="text-none font-weight-regular"
                icon="mdi-delete"
                variant="plain"
                size="small"
                @click="deleteRequest(item)"
              ></v-btn>
              <v-btn
                class="text-none font-weight-regular"
                icon="mdi-send"
                variant="plain"
                size="small"
                @click="sendToForPurchase(item)"
              ></v-btn>
            </template>
          </v-data-table>
        </v-window-item>
        
        <!-- IN PURCHASE -->
        <v-window-item :value="2">
          <v-data-table 
            v-model:search="search" 
            :items="inPurchaseRequests"
            :headers="headers"
          >
            <template v-slot:[`item.priority`]="{ item }">
              <v-chip :color="priorityColor(item)">
                {{ priorityLabel(item) }}
              </v-chip>
            </template>
            <template v-slot:[`item.action`]="{ item }">
              <v-btn
                class="text-none font-weight-regular"
                icon="mdi-eye"
                variant="plain"
                size="small"
                @click="viewRequest(item)"
              ></v-btn>
              <v-btn
                class="text-none font-weight-regular"
                icon="mdi-pencil"
                variant="plain"
                size="small"
                @click="editRequest(item)"
              ></v-btn>
              <v-btn
                class="text-none font-weight-regular"
                icon="mdi-send"
                variant="plain"
                size="small"
                @click="sendToApproval(item)"
              ></v-btn>
            </template>
          </v-data-table>
        </v-window-item>
      </v-window>
    </v-card-text>

    <RequestDetailsDialog 
      v-model="detailsDialog"
      :request="selectedRequest"
      @snackbar="snackbarHandler"
      @close="detailsDialog = false"
    />
    
    <EditRequestDialog 
      v-model="editDialog"
      :request="selectedRequest"
      @close="editDialog = false"
      @save="handleUpdateRequest"
    />

    <DeleteRequestDialog
      v-model="deleteDialog"
      :request="selectedRequest"
      @close="deleteDialog = false"
    />

    <ChangeStatusDialog
      v-model="changeStatusDialog"
      :request="selectedRequest"
      :newStatus="newStatus"
      @close="changeStatusDialog = false"
    />
    
    <v-snackbar
      v-model="snackbar.active"
      :timeout="4000"
      :color="snackbar.color"
      elevation="24"
    >
      {{ snackbar.text }}
    </v-snackbar>
  </v-card>
</template>

<script>
import NewRequestDialog from '@/components/Request/NewRequestDialog.vue';
import RequestDetailsDialog from '@/components/Request/RequestDetailsDialog.vue';
import EditRequestDialog from '@/components/Request/EditRequestDialog.vue';
import DeleteRequestDialog from '@/components/Request/DeleteRequestDialog.vue';
import ChangeStatusDialog from '@/components/Request/ChangeStatusDialog.vue';

import { REQUEST_STATUS, PRIORITY_TYPES } from '@/helpers/constants/app.constants';
import { mapState, mapGetters, mapActions } from 'vuex';

export default {
  name: 'RequestContent',

  components: { 
    NewRequestDialog,
    RequestDetailsDialog,
    EditRequestDialog,
    DeleteRequestDialog,
    ChangeStatusDialog
  },
  
  data: () => ({
    tab: 1,
    search: '',
    newStatus: '',
    detailsDialog: false,
    editDialog: false,
    deleteDialog: false,
    changeStatusDialog: false,
    selectedRequest: null,
    snackbar: {
      text: '',
      color: '',
      active: false
    }
  }),

  computed: {
    ...mapState('request', ['requests']),

    ...mapGetters('request', ['pendingRequests', 'inPurchaseRequests']),

    headers() {
      return [
        { title: 'Código', key: 'code' },
        { title: 'Setor', key: 'sector' },
        { title: 'Descrição', key: 'description' },
        { title: 'Requisitado', key: 'requestDate' },
        { title: 'Prioridade', key: 'priority' },
        { title: 'Ações', key: 'action', sortable: false }
      ]
    },
  },

  mounted() {
    this.initApp();
  },

  methods: {
    ...mapActions(['initApp']),

    ...mapActions('request', [
      'updateRequest',
      'updateStatus',
      'deleteRequest'
    ]),

    viewRequest(item) {
      this.selectedRequest = item;
      this.detailsDialog = true;
    },
    
    editRequest(item) {
      this.selectedRequest = item;
      this.editDialog = true;
    },

    deleteRequest(item) {
      this.selectedRequest = item;
      this.deleteDialog = true;
    },

    sendToForPurchase(item) {
      this.selectedRequest = item;
      this.changeStatusDialog = true;
      this.newStatus = REQUEST_STATUS.PENDING.id;
    },

    sendToApproval(item) {
      this.selectedRequest = item;
      this.changeStatusDialog = true;
      this.newStatus = REQUEST_STATUS[item.status].nextStatus;
    },

    priorityLabel(item) {
      return PRIORITY_TYPES[item.priority].label;
    },

    priorityColor(item) {
      return PRIORITY_TYPES[item.priority].color;
    },
    
    async handleUpdateRequest(requestData) {
      const { success, error } = await this.updateRequest(requestData);
      
      if (success) {
        this.snackbarHandler({ color: 'success', text: 'Requisição atualizada com sucesso!' });
      } else {
        this.snackbarHandler({ color: 'error', text: `Erro ao atualizar requisição: ${error}` });
      }
    },
    
    async markAsDelivered(item) {
      const { success, error } = await this.updateStatus({
        requestId: item.raw.id,
        status: REQUEST_STATUS.DELIVERED.id,
        comments: 'Produto entregue'
      });
      
      if (success) {
        this.snackbarHandler({ color: 'success', text: 'Requisição marcada como entregue!' });
      } else {
        this.snackbarHandler({ color: 'error', text: `Erro ao marcar como entregue: ${error}` });
      }
    },
    
    snackbarHandler({color, text}) {
      this.snackbar.color = color;
      this.snackbar.text = text;
      this.snackbar.active = true;
    }
  }
}
</script>